import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';

import { AppMessage } from '@/common/components';

import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';

import {
  SearchConsoleAccountsFormSecondStepFields,
  SearchConsoleAccountsFormSecondStepInputs,
  SearchConsoleAccountsQueryKeys,
  useGetByIdSearchConsoleAccountsQuery,
  useUpdateSearchConsoleAccountsMutation
} from '@/modules/DomainsApi';

import { SecondStepView } from './SecondStepView';
import { searchConsoleAccountsFormSecondStepValidationSchema } from './validations';

interface Props {
  searchConsoleAccountId: string;
}

export const SecondStepEditContainer: FC<Props> = memo(({ searchConsoleAccountId }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const appMessageApi = AppMessage.useControls();

  const [form] = Form.useForm<SearchConsoleAccountsFormSecondStepFields>();
  const [formErrors, setFormErrors] =
    useState<FormFieldsErrors<typeof SearchConsoleAccountsFormSecondStepInputs>>(null);

  const {
    data: searchConsoleAccountData,
    error: searchConsoleAccountError,
    isLoading: isSearchConsoleAccountFetching,
    isError: isSearchConsoleAccountError
  } = useGetByIdSearchConsoleAccountsQuery(searchConsoleAccountId)({
    enabled: !!searchConsoleAccountId,
    onSuccess: (data) => {
      if (!data.clientId && !data.clientSecret) {
        navigate(
          AppRoutes.DomainsApiSearchConsoleAccountsCreateCreds.replace(':searchConsoleAccountId', data.id),
          { replace: true }
        );
      }
    }
  });

  const {
    mutate: updateAccountMutation,
    isLoading: isUpdateAccountLoading
  } = useUpdateSearchConsoleAccountsMutation(searchConsoleAccountId)({
    onSuccess: async (data) => {
      appMessageApi.success('Search Console акаунт успішно оновлено!');

      queryClient.setQueryData([SearchConsoleAccountsQueryKeys.GetOneById, searchConsoleAccountId], data);

      await queryClient.refetchQueries([SearchConsoleAccountsQueryKeys.GetAll], { exact: false });

      navigate(AppRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SearchConsoleAccountsFormSecondStepInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: SearchConsoleAccountsFormSecondStepFields = {
    clientId: searchConsoleAccountData?.clientId,
    clientSecret: searchConsoleAccountData?.clientSecret
  };

  const handleFinish = useCallback((values: SearchConsoleAccountsFormSecondStepFields): void => {
    setFormErrors(null);
    updateAccountMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.DomainsApiSearchConsoleAccountsById.replace(':searchConsoleAccountId', searchConsoleAccountId));
  }, [searchConsoleAccountId]);

  return (
    <FallbackedLayout
      isLoading={ isSearchConsoleAccountFetching }
      isError={ isSearchConsoleAccountError }
      error={ searchConsoleAccountError }
    >
      {
        () => (
          <SecondStepView
            formName={ 'edit-search-console-account-form-second-step' }
            formType={ 'edit' }
            form={ form }
            initialValues={ initialValues }
            formErrors={ formErrors }
            isFormLoading={ isUpdateAccountLoading }
            validationSchema={ searchConsoleAccountsFormSecondStepValidationSchema }
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
          />
        )
      }
    </FallbackedLayout>
  );
});
